import { isInRange } from "../math"

/** Specific data needed to perform a validation to detect a Quality Problem  */
export type ValidationContext<D = Record<string, any>> = {
  /** Dictionary with CV detections */
  detections: D
}

/** A Quality Problem validation rule */
export interface ValidationRule<Context extends ValidationContext, Value extends any> {
  /** @returns value to be validated */
  value: (ctx: Context) => Value
  /**
   * Method performing the validation
   * @returns `true` if the value satisfies the validation rule `false` otherwise
   */
  validate: (value: Value, ctx: Context) => boolean
}

/** Generic validation */
export function Validation<Context extends ValidationContext, Value extends any>(options: {
  /** Abstract method for obtaining the value to be validated from the validation context */
  value: (ctx: Context) => Value
  /** Abstract method to perform the actual validation on the obtained value */
  validate: (value: Value, ctx: Context) => boolean
}): ValidationRule<Context, Value> {
  return options
}

/** Threshold-based validation */
export function ThresholdsValidation<Context extends ValidationContext>(options: {
  /** Abstract method for obtaining a numeric value to be validated from the validation context */
  value: (ctx: Context) => number
  /** Numeric thresholds to validate the obtained value against  */
  thresholds: ThresholdRanges
}): ValidationRule<Context, number> {
  return Validation<Context, number>({
    value: options.value,
    validate: ThresholdsValidator(options.thresholds),
  })
}

/**
 * Factory for creating {@link ThresholdsValidation}.validate() methods based on the given thresholds.
 *
 * The returned validation function forces the value to get into the "perfect" range first.
 * Once value gets into the "perfect" range, the function relaxes its validation and watches the value to be in the "good" range.
 * If value leaves the "good" range, the function returns to the initial state and starts forcing the value to be in the "perfect" range.
 *
 * @returns validation function
 */
function ThresholdsValidator(thresholds: ThresholdRanges) {
  let threshold: "good" | "perfect" = "perfect"

  return (value: number) => {
    const result = isInRange(value, ...thresholds[threshold])

    if (result && threshold === "perfect") threshold = "good"
    if (!result && threshold === "good") threshold = "perfect"

    return result
  }
}

export type ThresholdRange = [lower: number, upper: number]
export type ThresholdRanges = { good: ThresholdRange; perfect: ThresholdRange }

export const oneOf = (...rules: ValidationRule<any, any>[]) =>
  Validation({
    value: (ctx) => rules.map((rule) => rule.value(ctx)),
    validate: (values, ctx) => rules.some((rule, i) => rule.validate(values[i], ctx)),
  })
